import { Participant } from '../models';
import { MatchRepository, ParticipantRepository, TournamentRepository } from '../repositories';
import { logger } from '@/src/core/utils';

export class GetTournamentChampionUseCase {
  constructor(
    private matchRepository: MatchRepository,
    private participantRepository: ParticipantRepository,
    private tournamentRepository: TournamentRepository
  ) {}

  async execute(tournamentId: string): Promise<Participant | null> {
    logger.debug('GetChampion', 'Starting champion lookup', { tournamentId });

    // Get all matches for the tournament
    const matches = await this.matchRepository.getByTournamentId(tournamentId);
    logger.debug('GetChampion', 'Total matches found', { count: matches.length });

    // Try to find grand final (for Double Elimination)
    const grandFinals = matches
      .filter(m => m.bracketType === 'grand_final' && m.status === 'completed')
      .sort((a, b) => b.round - a.round);

    logger.debug('GetChampion', 'Grand finals found', { count: grandFinals.length });

    if (grandFinals.length > 0 && grandFinals[0].winnerId) {
      // Double Elimination - get champion from grand final
      const champion = await this.participantRepository.getById(grandFinals[0].winnerId);
      logger.debug('GetChampion', 'Champion from Grand Final', { name: champion?.name });
      return champion;
    }

    // Single Elimination - find the last completed winners bracket match (no next match)
    const winnersMatches = matches
      .filter(m => m.bracketType === 'winners' && m.status === 'completed')
      .sort((a, b) => b.round - a.round);

    logger.debug('GetChampion', 'Winners matches found', { count: winnersMatches.length });

    // Find the match that has no nextMatchId (it's the final)
    const finalMatch = winnersMatches.find(m => !m.nextMatchId);

    if (finalMatch && finalMatch.winnerId) {
      logger.debug('GetChampion', 'Champion from Winners Final', { winnerId: finalMatch.winnerId });
      const champion = await this.participantRepository.getById(finalMatch.winnerId);
      logger.debug('GetChampion', 'Champion resolved', { name: champion?.name });
      return champion;
    }

    // Round Robin - get the participant with most points
    const tournament = await this.tournamentRepository.getById(tournamentId);
    if (tournament?.tournamentType === 'round_robin') {
      logger.debug('GetChampion', 'Round Robin tournament detected');

      // We'll need to calculate standings here
      // For now, let's import the standings use case
      // This is a temporary solution - ideally we'd inject this dependency
      const { GetRoundRobinStandingsUseCase } = await import('./GetRoundRobinStandingsUseCase');
      const standingsUseCase = new GetRoundRobinStandingsUseCase(
        this.participantRepository,
        this.matchRepository
      );

      const standings = await standingsUseCase.execute(tournamentId);
      if (standings.length > 0 && standings[0].played > 0) {
        logger.debug('GetChampion', 'Round Robin champion found', { name: standings[0].participant.name });
        return standings[0].participant;
      }
    }

    logger.debug('GetChampion', 'No champion found');
    return null;

  }
}
